/**
 * SCRUM-241 — Structura Picking: pedidos pendientes de preparación.
 *
 * La cola excluye las ventas de mostrador (se entregan en el acto, no hay nada
 * que preparar) y arranca en CONFIRMADO, no en PENDIENTE. No hay asignación por
 * operador en este MVP: todos ven la misma lista.
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PickingShell } from "@/components/picking/PickingShell";
import { pickingJson } from "@/lib/picking/session";
import { ORDER_STATUS_LABELS, PICKING_QUEUE_STATUSES } from "@/domain/sm-models";

export const Route = createFileRoute("/picking/pedidos/")({
  component: PedidosPage,
  head: () => ({ meta: [{ title: "Pedidos — Structura Picking" }] }),
});

interface QueueItem {
  id: string;
  cantidad: number;
  qtyPicked: number | null;
}

interface QueueOrder {
  id: string;
  orderNumber: number | null;
  customerName: string;
  status: string;
  createdAt?: string;
  items?: QueueItem[];
}

function progressOf(order: QueueOrder): { done: number; total: number } {
  const items = order.items ?? [];
  return { done: items.filter((i) => i.qtyPicked != null).length, total: items.length };
}

function PedidosPage() {
  const [orders, setOrders] = useState<QueueOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = PICKING_QUEUE_STATUSES.map((s) => `status=${encodeURIComponent(s)}`).join("&");
      const data = await pickingJson<{ orders?: QueueOrder[] } | QueueOrder[]>(
        `/api/orders?${qs}&includeItems=1&excludePos=1&limit=100`,
      );
      const list = Array.isArray(data) ? data : (data.orders ?? []);
      setOrders(list);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudieron cargar los pedidos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PickingShell
      title="Pedidos"
      backTo="/picking"
      actions={
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9 shrink-0"
          onClick={() => void load()}
          aria-label="Actualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      }
    >
      {() =>
        loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : orders.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No hay pedidos pendientes de preparación.
          </p>
        ) : (
          <div className="space-y-2">
            {orders.map((order) => {
              const { done, total } = progressOf(order);
              return (
                <Link
                  key={order.id}
                  to="/picking/pedidos/$orderId"
                  params={{ orderId: order.id }}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 active:bg-accent"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">
                        #{order.orderNumber ?? "—"}
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {ORDER_STATUS_LABELS[order.status as keyof typeof ORDER_STATUS_LABELS] ??
                          order.status}
                      </Badge>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{order.customerName}</p>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {done}/{total}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Link>
              );
            })}
          </div>
        )
      }
    </PickingShell>
  );
}
